import {
  FormatDateOptions,
  FormatNumberOptions,
  FormatRelativeTimeOptions,
  PrimitiveType,
  useIntl,
} from 'react-intl';
import {
  selectUnit,
  generateTranslationKey,
  LinkParams,
  dateInDbFormat,
  generateLink,
} from './utils';

interface CurrencyFormatOptions extends FormatNumberOptions {
  currency?: string;
}

interface RelativeDateOptions {
  dateString: string;
  showTime?: boolean;
}

/**
 * Return type of the useI18n hook
 */
interface I18nUtils {
  elasticDateFormat: (date: string | number | Date) => string;
  formatCurrency: (value: number, options?: CurrencyFormatOptions) => string;
  formatNumber: (value: number, options?: FormatNumberOptions) => string;
  formatRelativeDate: (options: RelativeDateOptions) => string | undefined;
  formatRelativeTime: (
    value: number,
    unit: Intl.RelativeTimeFormatUnit,
    options?: FormatRelativeTimeOptions
  ) => string;
  formatTime: (
    value: Date | number | string,
    options?: FormatDateOptions
  ) => string;
  formattedDate: (
    date: string | number | Date,
    options?: FormatDateOptions
  ) => string;
  generateLink: (params: LinkParams) => string;
  locale: string;
  timeAgo: (date: string | number | Date) => string;
  translate: (id: string, opts?: Record<string, PrimitiveType>) => string;
  translationKey: (id: string) => string;
}

/**
 * A hook which provides i18n utilities on top of react-intl.
 * Supports namespace prefixing for ids that start with '.'.
 *
 * Usage examples:
 *     let { translate } = useI18n();
 *     translate('app.actions.edit')
 *
 *     let { translate } = useI18n('app.actions');
 *     translate('.edit'); // Evaluates to app.actions.edit
 *     translate('no.prefix'); // Evaluates to no.prefix
 *
 *     let { formatCurrency } = useI18n();
 *     formatCurrency(300) // €300.00
 */
const useI18n = (namespace?: string): I18nUtils => {
  const {
    formatDate,
    formatMessage,
    formatNumber,
    formatRelativeTime,
    formatTime,
    locale,
  } = useIntl();

  const translationKey = generateTranslationKey(namespace);
  const translate = (
    id: string,
    opts?: Record<string, PrimitiveType>
  ): string => {
    const translationId = translationKey(id);
    return formatMessage({ id: translationId }, opts);
  };

  // Date utilities
  const formattedDate = (
    date: string | number | Date,
    options: FormatDateOptions = {}
  ): string => {
    const defaultOptions: FormatDateOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    };
    return formatDate(date, { ...defaultOptions, ...options });
  };

  const elasticDateFormat = (date: string | number | Date): string => {
    const dateStr = typeof date === 'string' ? date : date.toString();
    const todayInStr = dateInDbFormat(new Date());
    if (dateStr.search(todayInStr) > -1) return translate('app.today');
    const yesterdayInStr = dateInDbFormat(
      new Date(new Date().getTime() - 86400 * 1000)
    );
    if (dateStr.search(yesterdayInStr) > -1) return translate('app.yesterday');
    return formattedDate(date);
  };

  const formatRelativeDate = ({
    dateString,
    showTime,
  }: RelativeDateOptions): string | undefined => {
    if (!dateString) return undefined;
    const time = showTime ? `, ${formatTime(dateString)}` : '';
    const distance = Math.ceil(
      (new Date().getTime() - new Date(dateString).getTime()) / (86400 * 1000)
    );
    const date =
      distance > 2 && distance < 8
        ? formatRelativeTime(distance * -1, 'day')
        : elasticDateFormat(dateString);
    return `${date}${time}`;
  };

  // Time utilities
  const timeAgo = (date: string | number | Date): string => {
    const { value, unit } = selectUnit(new Date(date));
    return formatRelativeTime(value, unit);
  };

  // Currency utilities
  const formatCurrency = (
    number: number,
    options?: CurrencyFormatOptions
  ): string =>
    formatNumber(number, { style: 'currency', currency: 'EUR', ...options });

  return {
    elasticDateFormat,
    formatCurrency,
    formatNumber,
    formatRelativeDate,
    formatRelativeTime,
    formatTime,
    formattedDate,
    generateLink,
    locale,
    timeAgo,
    translate,
    translationKey,
  };
};

export default useI18n;
